// ref: https://codepen.io/shshaw/pen/vYKBPbv/9e810322d70c306de2d18237d0cb2d78

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { useTranslation } from 'next-i18next';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';

import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from 'framer-motion';

import BezierEasing from 'bezier-easing';

import {
  StaggeredDelayedAnimation,
  AnimatedWords,
} from '../StaggeredDelayedAnimation/StaggeredDelayedAnimation';

import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ZoomLevelContext } from '../../containers/BasePage';
import useMediaQuery from '../../hooks/useMediaQuery';
import useElementScrollPosition from '../../hooks/useElementScrollPosition';

import { mapRange, easeOutCubic } from '../../utils/helpers';
import { trackEvent } from '../../utils/dataLayer';

// slow down video 'scroll' speed when we reach the end, see:
// https://cubic-bezier.com/#.93,.91,.81,.99
const videoBezierCurve = BezierEasing(0.93, 0.91, 0.81, 0.99);

import s from './ScrolledVideoHero.module.scss';

// IMPORTANT:
// The encoding is super important here to enable frame-by-frame scrubbing:
// ffmpeg -i ~/Downloads/Toshiba\ video/original.mov -movflags faststart -vcodec libx264 -crf 23 -g 1 -pix_fmt yuv420p output.mp4
// ffmpeg -i ~/Downloads/Toshiba\ video/original.mov -vf scale=960:-1 -movflags faststart -vcodec libx264 -crf 20 -g 1 -pix_fmt yuv420p output_960.mp4

const DEFAULT_VIDEO_RESP = '/video/scroll-hero--resp--frames-fixed.mp4';
const DEFAULT_VIDEO = '/video/scroll-hero--1920p--frames-fixed.mp4';

const exitAnimProps = {
  opacity: 0,
  transition: {
    duration: 0.15,
  },
};

const loaderProgressInitialAnimProps = {
  opacity: 0,
  y: 10,
};

const loaderProgressEnteredAnimProps = {
  opacity: 1,
  skew: 0,
  y: 0,
  transition: {
    duration: 0.2,
  },
};

const loaderLabelInitialAnimProps = {
  opacity: 0,
  y: 5,
};

const loaderLabelEnteredAnimProps = {
  opacity: 1,
  skew: 0,
  y: 0,
  transition: {
    duration: 0.1,
    delay: 0.2,
  },
};

const headingInitialAnimProps = {
  opacity: 0,
  skew: 13,
  y: 20,
};

const headingEnteredAnimProps = {
  opacity: 1,
  skew: 0,
  y: 0,
  transition: {
    duration: 0.5,
    delay: 0.2,
  },
};

const preambleInitialAnimProps = {
  opacity: 0,
  y: 20,
};

const preambleEnteredAnimProps = {
  opacity: 1,
  y: 0,
  transition: {
    delay: 0.95,
    duration: 0.5,
  },
};

const introPreambleEnteredAnimProps = {
  ...preambleEnteredAnimProps,
  transition: {
    delay: 0.4,
    duration: 0.5,
  },
};

const ScrolledVideoHero = (props) => {
  const {
    titleCopy,
    sceneCopy,
    videoSrcResp,
    videoSrc,
    scrollHeightResp,
    scrollHeight,
  } = props;

  const { t } = useTranslation();
  const largeVideoMq = useMediaQuery('(min-width: 435px)');

  const currentScrollHeight =
    largeVideoMq || !scrollHeightResp ? scrollHeight : scrollHeightResp;

  const [videoLoaded, setVideoLoaded] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [videoNode, setVideoNode] = useState(null);
  const [currentVideoSrc, setCurrentVideoSrc] = useState(videoSrcResp);
  const browserZoomPercentage = useContext(ZoomLevelContext);

  const [loadProgress, setLoadProgress] = useState(0);
  const loadProgressRef = useRef(0);

  const videoRefFn = useCallback((_videoNode) => {
    setVideoNode(_videoNode);
  }, []);

  const scrollElementRef = useRef();
  const scrolledElementPosition = useElementScrollPosition(scrollElementRef);

  // handle scroll lock on load

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!videoLoaded) {
      disablePageScroll();
    } else {
      setTimeout(() => {
        enablePageScroll();
      }, 500);
    }
  }, [videoLoaded]);

  // handle video source change

  useEffect(() => {
    setCurrentVideoSrc(largeVideoMq || !videoSrcResp ? videoSrc : videoSrcResp);
  }, [largeVideoMq]);

  // init video

  useEffect(() => {
    if (!videoNode || videoLoaded) {
      return;
    }

    videoNode.playbackRate = 5.0;

    const activateVideo = () => {
      videoNode.play();
      setTimeout(() => {
        videoNode.currentTime = 0;
        videoNode.pause();
        document.removeEventListener('touchstart', activateVideo);
      }, 0);
    };

    // ios
    document.addEventListener('touchstart', activateVideo);
    return () => {
      document.removeEventListener('touchstart', activateVideo);
    };
  }, [videoNode]);

  // handle video loaded event

  const handleVideoProgress = ({ type }) => {
    if (loadProgressRef.current >= 99) {
      return;
    }

    switch (type) {
      case 'loadStart':
        loadProgressRef.current += 10;
        setLoadProgress(loadProgressRef.current);
        break;

      case 'canplay':
        loadProgressRef.current + 10;
        setLoadProgress(loadProgressRef.current);
        break;

      default:
      case 'progress':
        loadProgressRef.current += 15;
        setLoadProgress(loadProgressRef.current);
        break;
    }
  };

  const videoHasLoaded = () => {
    const intervalId = setInterval(() => {
      if (videoNode.readyState > 3) {
        clearInterval(intervalId);

        loadProgressRef.current = 100;
        setLoadProgress(loadProgressRef.current);

        videoNode.removeEventListener('loadStart', handleVideoProgress);
        videoNode.removeEventListener('progress', handleVideoProgress);
        videoNode.removeEventListener('canplay', handleVideoProgress);
        videoNode.removeEventListener('canplaythrough', videoHasLoaded);

        // make sure loader is actually perceived
        // and doesn't just flicker by
        setTimeout(() => {
          videoNode.pause();
          videoNode.currentTime = 0;

          enablePageScroll();
          setVideoLoaded(true);
        }, 1000);
      }
    }, 250);
  };

  useEffect(() => {
    if (!videoNode) {
      return;
    }

    // if the video is in the cache of the browser,
    // the 'canplaythrough' event might have been triggered
    // before we registered the event handler.
    if (videoNode.readyState > 3) {
      videoHasLoaded();
      return;
    }

    videoNode.addEventListener('loadStart', handleVideoProgress);
    videoNode.addEventListener('progress', handleVideoProgress);
    videoNode.addEventListener('canplay', handleVideoProgress);
    videoNode.addEventListener('canplaythrough', videoHasLoaded);

    return () => {
      videoNode.removeEventListener('loadStart', handleVideoProgress);
      videoNode.removeEventListener('progress', handleVideoProgress);
      videoNode.removeEventListener('canplay', handleVideoProgress);
      videoNode.removeEventListener('canplaythrough', videoHasLoaded);
    };
  }, [videoNode]);

  // set current video time based on y scroll pos

  useEffect(() => {
    if (!videoNode) {
      return;
    }

    const currentTime = (
      videoBezierCurve(scrolledElementPosition.scrolledP) * videoNode.duration
    ).toFixed(2);

    if (
      isNaN(currentTime) ||
      currentTime < 0 ||
      currentTime >= videoNode.duration
    ) {
      return;
    }

    setCurrentVideoTime(currentTime);
  }, [scrolledElementPosition, videoNode]);

  useEffect(() => {
    if (!videoNode) {
      return;
    }

    videoNode.currentTime = currentVideoTime;
  }, [currentVideoTime, videoNode]);

  // event handlers

  const handleExperienceScroll = () => {
    const windowInnerHeight =
      typeof window !== 'undefined' ? window.innerHeight : 1045;
    const maxScrollPx = (currentScrollHeight / 100) * windowInnerHeight;

    trackEvent('scrollButton');

    window.scroll({
      top: Math.min(
        window.scrollY + window.innerHeight * (scrollHeight / 400),
        maxScrollPx
      ),
      left: 0,
      behavior: 'smooth',
    });
  };

  // classes

  const videoOverlayCls = classNames(s.VideoOverlay, {
    [s.VideoOverlayLocked]: scrolledElementPosition.scrolledP >= 1,
  });

  const startCopyVisible = scrolledElementPosition.scrolledP <= 0;
  const introVisible = videoLoaded && startCopyVisible;

  const scenes = sceneCopy.map((scene) => ({
    ...scene,
    active:
      currentVideoTime > scene.startTime && currentVideoTime < scene.endTime,
  }));

  // fade overlay

  const { scrollY } = useScroll();
  const fadeYEased = useTransform(scrollY, (v) => {
    const windowInnerHeight =
      typeof window !== 'undefined' ? window.innerHeight : 2000;
    const scrollHeightPx = (currentScrollHeight / 100 - 0) * windowInnerHeight;

    const scrollYPx = scrollY.get();
    const pastContainerBottom =
      scrollYPx - (scrollHeightPx - windowInnerHeight);
    const fadeY = mapRange(pastContainerBottom, 0, windowInnerHeight, 0, 1);
    const eased =
      -1 * easeOutCubic(Math.min(fadeY, 1)) * windowInnerHeight * 0.7;

    return eased;
  });

  const loaderBgCls = classNames(s.LoaderBg, {
    [s.Hidden]: videoLoaded,
  });

  const rootCls = classNames(s.Root, {
    [s.ZoomLevel200]: browserZoomPercentage >= 190,
    [s.ZoomLevel300]: browserZoomPercentage >= 290,
    [s.ZoomLevel300]: browserZoomPercentage >= 290,
    [s.ZoomLevel400]: browserZoomPercentage >= 390,
  });

  return (
    <>
      <div className={loaderBgCls} />
      <div className={rootCls}>
        <div
          ref={scrollElementRef}
          className={s.Container}
          style={{
            minHeight: `${currentScrollHeight}vh`,
            height: `${currentScrollHeight}vh`,
          }}>
          {videoLoaded && scrolledElementPosition.scrolledP > 0 && (
            <motion.div
              className={s.FadeOverlay}
              style={{
                y: fadeYEased,
              }}
            />
          )}
        </div>

        <AnimatePresence>
          {!videoLoaded && loadProgress !== null && (
            <div className={s.VideoLoaderWrapper}>
              <div className={s.VideoLoader}>
                <motion.span
                  className={s.VideoLoaderText}
                  initial={loaderLabelInitialAnimProps}
                  animate={loaderLabelEnteredAnimProps}
                  exit={exitAnimProps}>
                  {t('Insights.loadingExperience')}
                </motion.span>
                <motion.span
                  className={s.VideoLoaderProgress}
                  initial={loaderProgressInitialAnimProps}
                  animate={loaderProgressEnteredAnimProps}
                  exit={exitAnimProps}>
                  {loadProgress}%
                </motion.span>
              </div>
            </div>
          )}
        </AnimatePresence>

        <div className={videoOverlayCls}>
          <AnimatePresence>
            {introVisible && (
              <motion.div
                key="intro-1"
                className={s.StartCopyContainer}
                exit={exitAnimProps}>
                <div className={s.StartCopyInner} aria-hidden={!introVisible}>
                  <motion.h1
                    className={s.MainHeading}
                    initial={headingInitialAnimProps}
                    animate={headingEnteredAnimProps}>
                    {titleCopy.boldTitle}&nbsp;
                    <span className={s.MainHeadingRegular}>
                      {titleCopy.regularTitle}
                    </span>
                  </motion.h1>
                  <motion.p
                    className={s.HeadingPreamble}
                    initial={preambleInitialAnimProps}
                    animate={introPreambleEnteredAnimProps}>
                    {titleCopy.preamble}
                  </motion.p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            className={s.ScrollIndicatorWrapper}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
              transition: {
                duration: 0.3,
                delay: 1,
              },
            }}>
            <div className={s.ScrollLabelContainer}>
              <AnimatePresence>
                {scrolledElementPosition.scrolledP <= 0 && (
                  <motion.span
                    key="initial-scroll"
                    className={s.ScrollLabel}
                    initial={{
                      opacity: 0,
                      y: -5,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        delay: 0.2,
                        duration: 0.2,
                      },
                    }}
                    exit={{
                      opacity: 0,
                      y: -5,
                    }}>
                    {t('Insights.scrollToExplore')}
                  </motion.span>
                )}

                {scrolledElementPosition.scrolledP > 0 && (
                  <motion.span
                    key="keep-scrolling"
                    className={s.ScrollLabel}
                    initial={{
                      opacity: 0,
                      y: 5,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.2,
                        delay: 0.2,
                      },
                    }}
                    exit={{
                      opacity: 0,
                      y: 5,
                    }}>
                    {t('Insights.keepScrolling')}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <button
              onClick={handleExperienceScroll}
              className={s.ScrollIndicatorButton}
            />
          </motion.div>

          <AnimatePresence>
            {scenes.length &&
              !introVisible &&
              scenes.map((scene) => {
                if (!scene.active) {
                  return;
                }

                const titleCls = classNames(s.SceneTitle, 'grid');

                return (
                  <div
                    className={s.SceneContainer}
                    key={scene.boldTitle}
                    aria-hidden={!scene.active}>
                    <StaggeredDelayedAnimation stagger={0.04}>
                      <h1 className={titleCls}>
                        <span className={s.SceneTitleTop}>
                          <AnimatedWords text={scene.boldTitle} />
                        </span>
                        <span className={s.SceneTitleBottom}>
                          <AnimatedWords text={scene.regularTitle} />
                        </span>
                      </h1>
                    </StaggeredDelayedAnimation>
                    <div className="grid">
                      <motion.p
                        className={s.SceneText}
                        initial={preambleInitialAnimProps}
                        animate={preambleEnteredAnimProps}>
                        {scene.text}
                      </motion.p>
                    </div>
                  </div>
                );
              })}
          </AnimatePresence>

          <div className={s.VideoInner}>
            <video
              data-matomo-ignore
              ref={videoRefFn}
              src={currentVideoSrc}
              playsInline
              autoPlay
              preload="auto"
              muted={true}></video>
          </div>
        </div>
      </div>
    </>
  );
};

ScrolledVideoHero.propTypes = {
  titleCopy: PropTypes.object.isRequired,
  scrollHeight: PropTypes.number.isRequired,
  scrollHeightResp: PropTypes.number,
  videoSrc: PropTypes.string.isRequired,
  videoSrcResp: PropTypes.string,
  sceneCopy: PropTypes.array,
};

ScrolledVideoHero.defaultProps = {
  titleCopy: {
    regularTitle: '',
    boldTitle: '',
  },
  videoSrc: DEFAULT_VIDEO,
  videoSrcResp: DEFAULT_VIDEO_RESP,
  scrollHeightResp: null,
  scrollHeight: 800,
  sceneCopy: [],
};

export default ScrolledVideoHero;
